import { useCallback, useEffect, useMemo } from "react"
import { useRecoilState } from "recoil"
import { throttle } from "lodash"
import { ScaleTime } from "d3"

import { eegCalibrationAtom } from "../Atoms/Calibration"
import { DEFAULT_PX_PER_MM } from "./constants"
import { clearCalibration, loadCalibration, saveCalibration } from "./storage"
import { EEGCalibrationStatus } from "./types"
import {
	createCalibrationState,
	evaluateCalibrationStatus,
	missingCalibrationState,
} from "./state"
import { getClampedStartAndEnd } from "../Components/Visualizations/clamping"

/**
 * Exposes the current calibration status and provides methods for managing calibrations
 */
export const useEEGCalibration = () => {
	const [state, setState] = useRecoilState(eegCalibrationAtom)

	const pxPerMm = useMemo(() => {
		if (state.status === EEGCalibrationStatus.VALID && state.record) {
			return state.record.pxPerMm
		}
		return DEFAULT_PX_PER_MM
	}, [state])

	const save = useCallback(
		(pxPerMm: number) => {
			const result = saveCalibration(pxPerMm)
			result
				? setState(createCalibrationState(result.record, result.key))
				: setState(missingCalibrationState)
		},
		[setState],
	)

	const clear = useCallback(() => {
		if (state.storageKey !== undefined) {
			clearCalibration(state.storageKey)
		}
		setState(missingCalibrationState)
	}, [setState, state.storageKey])

	const refreshStatus = useCallback(() => {
		setState(previous => {
			if (!previous.record) {
				// no previous calibration state -> load from localstorage
				const loaded = loadCalibration()
				return loaded
					? createCalibrationState(loaded.record, loaded.key)
					: missingCalibrationState
			}

			const currentStatus = evaluateCalibrationStatus(previous.record)

			if (
				currentStatus === previous.status &&
				currentStatus !== EEGCalibrationStatus.OUTDATED
			) {
				// current status is still valid
				return previous
			}

			if (currentStatus === EEGCalibrationStatus.OUTDATED) {
				// current status is outdated -> load
				const loaded = loadCalibration()

				if (loaded && loaded.key !== previous.storageKey) {
					// found a calibration matching the new environment
					return createCalibrationState(loaded.record, loaded.key)
				}
			}

			return { ...previous, status: currentStatus }
		})
	}, [setState])

	const pxFromMm = useCallback((mm: number) => mm * pxPerMm, [pxPerMm])

	return {
		status: state.status,
		record: state.record,
		pxPerMm,
		save,
		clear,
		refreshStatus,
		pxFromMm,
	}
}

/**
 * Calls refreshStatus whenever the browser or viewport is resized or if the resolution changes
 */
export const useEEGCalibrationWatcher = () => {
	const { record, refreshStatus } = useEEGCalibration()

	useEffect(() => {
		const throttledRefresh = throttle(() => refreshStatus(), 200, {
			leading: true, // exec on first trigger
			trailing: true, // exec once after last trigger
		})

		// Load initial calibration if missing
		if (!record) {
			refreshStatus()
		}

		window.addEventListener("resize", throttledRefresh)

		// visualViewport may not be available in all browsers
		if (window.visualViewport) {
			window.visualViewport.addEventListener("resize", throttledRefresh)
		}

		// detect resolution changes with a media query for the current resolution
		const mediaQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)

		// Older browsers use addListener/removeListener instead of addEventListener/removeEventListener
		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener("change", throttledRefresh)
		} else if (mediaQuery.addListener) {
			mediaQuery.addListener(throttledRefresh)
		}

		return () => {
			window.removeEventListener("resize", throttledRefresh)
			window.visualViewport?.removeEventListener("resize", throttledRefresh)

			if (mediaQuery.removeEventListener) {
				mediaQuery.removeEventListener("change", throttledRefresh)
			} else if (mediaQuery.removeListener) {
				mediaQuery.removeListener(throttledRefresh)
			}
		}
	}, [record, refreshStatus])
}

/**
 * Maintains a constant mm/s window length when the chart width changes
 * Only active when calibrated and targetMmPerSecond is set.
 */
export const useMaintainMmPerSecond = (
	targetMmPerSecond: number | undefined,
	calibrationStatus: EEGCalibrationStatus,
	pxPerMm: number,
	chartWidthPx: number,
	viewScale: ScaleTime<any, any, any>,
	fileScale: ScaleTime<any, any, any>,
	setConfig: (updater: (prev: any) => any) => void,
) => {
	useEffect(() => {
		// only maintain mm/s if we have a target and the display is calibrated
		if (
			!targetMmPerSecond ||
			pxPerMm <= 0 ||
			chartWidthPx <= 0 ||
			calibrationStatus !== EEGCalibrationStatus.VALID
		) {
			return
		}

		const idealDurationMs = Math.round((chartWidthPx / pxPerMm / targetMmPerSecond) * 1000)
		if (!Number.isFinite(idealDurationMs) || idealDurationMs <= 0) return

		const [currentStart] = viewScale.domain()
		const startTime = currentStart.getTime()
		const { start, end } = getClampedStartAndEnd(
			startTime,
			startTime + idealDurationMs,
			fileScale,
		)

		const actualDuration = end - start
		const currentDuration = viewScale.domain()[1].getTime() - startTime

		// only update if duration need adjustment
		if (actualDuration <= 0 || Math.abs(currentDuration - actualDuration) <= 1) return

		setConfig(prev => ({
			...prev,
			viewScale: prev.viewScale.domain([start, end]),
		}))
	}, [
		targetMmPerSecond,
		calibrationStatus,
		pxPerMm,
		chartWidthPx,
		viewScale,
		fileScale,
		setConfig,
	])
}
